import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ListItem } from '@melio/penny';
import { convertAmountToCurrency } from '@melio/platform-utils';

import { usePlatformIntl } from '@/translations/Intl';
import { SummaryTypeEnum } from '@/types/vendors.types';

export interface PaymentSummaryProps {
  summaryType: SummaryTypeEnum;
  link: string;
  sum?: number;
  vendorName?: string;
  count?: number;
  onLinkClick?: () => void;
}

export const PaymentSummary = ({
  summaryType,
  link,
  vendorName,
  sum = 0,
  count = 0,
  onLinkClick,
}: PaymentSummaryProps) => {
  const navigate = useNavigate();
  const { formatMessage, formatCurrency } = usePlatformIntl();

  const descriptionLabel = formatMessage(
    `widgets.paymentOverview.${summaryType === SummaryTypeEnum.OPEN_BALANCE ? 'bills' : 'payments'}`,
    { count },
  );
  const actionAriaLabel =
    summaryType === SummaryTypeEnum.OPEN_BALANCE
      ? formatMessage('widgets.paymentOverview.bills.action.ariaLabel', {
          count,
          vendorName,
          balance: formatCurrency(convertAmountToCurrency(sum)),
        })
      : undefined;

  const onClick = React.useCallback(() => {
    onLinkClick?.();
    navigate(link);
  }, [link, navigate, onLinkClick]);

  const descriptionProps = {
    label: descriptionLabel,
    'data-testid': `payment-overview-sum-${summaryType}-description`,
  };

  return (
    <ListItem
      // @ts-expect-error TODO ask ds to allow flex prop on list item
      flex={1}
      data-testid={`payment-overview-sum-${summaryType}-list-item`}
      labelProps={{
        label: formatMessage(`widgets.paymentOverview.${summaryType}.label`),
      }}
      mainLabelProps={{
        'data-testid': `payment-overview-sum-${summaryType}`,
        label: formatCurrency(convertAmountToCurrency(sum)),
      }}
      descriptionProps={
        count
          ? {
              ...descriptionProps,
              action: {
                'data-testid': `payment-summary-view-btn-${summaryType}`,
                label: formatMessage('widgets.paymentOverview.buttonLink'),
                'aria-label': actionAriaLabel,
                onClick,
              },
            }
          : descriptionProps
      }
    />
  );
};
